import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';

import { Route, Switch } from '@treams/treams-routes';
import { CardList, Container, Jumbo, Pill } from '@treams/treams-ui';
import { GiftIcon } from '@treams/treams-icons';

import * as graphql from 'generated/graphql';
import ComplimentCreateSelector from 'packages/kakapo-ui-application/pages/compliments/ComplimentCreateSelector';
import { ComplimentCreateModalWithID } from 'packages/kakapo-ui-application/pages/compliments/ComplimentCreateModal';
import { ComplimentCreateThankYouWithID } from 'packages/kakapo-ui-application/pages/compliments/ComplimentThankYou';

import UserFeedbackTabs from './UserFeedbackTabs';
import UserFeedbackButtons from './UserFeedbackButtons';
import MutualComplimentsList from './MutualComplimentsList';
import IncomingComplimentCard from './IncomingComplimentCard';
import OutgoingComplimentCard from './OutgoingComplimentCard';

type RouteParams = {
	id: string;
};

export default function UserCompliments() {
	const { t } = useTranslation();

	const { params } = useRouteMatch<{ id: string }>();
	const userID = params.id;

	const userQuery = graphql.useUserQuery({ variables: { id: userID } });

	const query = graphql.useUserComplimentsQuery({
		variables: { last: 100, id: userID },
	});
	const givenQuery = graphql.useUserGivenComplimentsQuery({
		variables: { last: 100, id: userID },
	});
	const viewerGivenQuery = graphql.useViewerGivenComplimentsQuery({
		variables: { last: 100, id: userID },
	});

	return (
		<>
			<Jumbo
				title={
					userQuery.data?.user.viewerCanViewFeedback
						? t('user.feedback.full.compliments.title', {
								firstName: userQuery.data?.user.firstName,
						  })
						: t('user.feedback.compliments.title', { name: userQuery.data?.user.firstName })
				}
				button={<UserFeedbackButtons user={userQuery.data?.user as graphql.User} />}
				alignItems="flex-start"
				footer={<UserFeedbackTabs user={userQuery.data?.user as graphql.User} />}
			/>

			{userQuery.data?.user.viewerCanViewFeedback ? (
				<>
					<Container mt={4} mb={-3}>
						<Pill>{t('feedback.compliments.section.incoming')}</Pill>
					</Container>
					<CardList
						translationName="compliments.incoming"
						query={query}
						path="user.compliments"
						icon={<GiftIcon />}
						renderCard={(item: graphql.Compliment) => (
							<IncomingComplimentCard key={item.id} userID={userID} compliment={item} />
						)}
						renderSkeleton={() => <IncomingComplimentCard.Skeleton />}
					/>

					<Container mt={4} mb={-3}>
						<Pill>{t('feedback.compliments.section.outgoing')}</Pill>
					</Container>
					<CardList
						translationName="compliments.outgoing"
						query={givenQuery}
						path="user.givenCompliments"
						icon={<GiftIcon />}
						renderCard={(item: graphql.Compliment) => (
							<OutgoingComplimentCard key={item.id} compliment={item} />
						)}
						renderSkeleton={() => <OutgoingComplimentCard.Skeleton />}
					/>
				</>
			) : (
				<MutualComplimentsList query={viewerGivenQuery} userID={userID} />
			)}

			<Switch>
				<Route path="/feedback/compliments/new">
					<ComplimentCreateSelector />
				</Route>
				<Route<RouteParams> path="/users/:id/feedback/compliments/new">
					{({ match }) => <ComplimentCreateModalWithID userID={match!.params.id!} />}
				</Route>
				<Route<RouteParams> path="/users/:id/feedback/compliments/thank-you">
					{({ match }) => <ComplimentCreateThankYouWithID userID={match!.params.id!} />}
				</Route>
			</Switch>
		</>
	);
}
